import { useState, useEffect } from 'react'
import PageNavigation from '../../../components/PageNavigation/PageNavigation'
import usage1 from '../../../assets/usage1.png'
import alert1 from '../../../assets/alert1.png'
import alert2 from '../../../assets/alert2.png'
// TODO: Add billing dashboard image (usage2.png or billing-dashboard.png)
// import billingDashboard from '../../../assets/usage2.png'
import './Usage.css'

const Usage = () => {
  const [activeSection, setActiveSection] = useState('overview')

  const sections = [
    { id: 'overview', title: 'Overview' },
    { id: 'checking-usage', title: 'Checking Usage' },
    { id: 'free-credits', title: 'Free Credits' },
    { id: 'usage-dashboard', title: 'Usage Dashboard' },
    { id: 'cost-tracking', title: 'Cost Tracking' },
    { id: 'service-breakdown', title: 'Service Breakdown' },
    { id: 'alerts', title: 'Alerts & Notifications' }
  ]

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(`usage-${sections[i].id}`)
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [sections])

  return (
    <div className="usage-page">
      <div className="usage-content-wrapper">
        <div className="usage-main-content">
          <h1>Usage & Alerts</h1>
      <p className="intro">
            Monitor your resource consumption, track costs, and configure alerts to stay informed about 
            your infrastructure on Aedify.AI.
          </p>

          <section id="usage-overview" className="usage-section">
            <h2>Overview</h2>
            <p>
              The Usage dashboard provides comprehensive insights into your resource consumption and costs. 
              Track usage across all services, monitor spending, and set up alerts to stay informed about 
              important events.
            </p>
            <p>
              Usage metrics are tracked in real-time and aggregated for daily, weekly, and monthly views. 
              You can view detailed breakdowns by service, resource type, and time period.
      </p>
          </section>

          <section id="usage-checking-usage" className="usage-section">
            <h2>Checking Usage</h2>
        <p>
              You can view your usage and remaining free credits in the <strong>Usage</strong> section of 
              the <strong>Aedify.AI dashboard</strong>. The Usage page provides detailed insights into your 
              resource consumption and credit balance.
            </p>
            <p>
              Usage tracking works as follows:
        </p>
        <ul>
              <li><strong>Machine usage (including memory):</strong> Tracked per second for accurate billing</li>
              <li><strong>Volumes and Machine count:</strong> Tracked per hour</li>
              <li><strong>Storage:</strong> Tracked per GB-hour</li>
              <li><strong>Network:</strong> Tracked per GB transferred</li>
        </ul>
            <p>
              Free credits are meant for kicking the tires. Spin something up, poke around, deploy a real 
              app or two. If Aedify.AI fits your use case, great! Add a credit card to keep going and receive 
              bonus credits. If not, no hard feelings.
            </p>
      </section>

          <section id="usage-free-credits" className="usage-section">
            <h2>What Happens When Free Credits Run Out?</h2>
            <p>
              When you <strong>use up your free credits</strong> and don't have a payment method on file, then:
            </p>
            <ul>
              <li><strong>Your apps will stop running</strong></li>
              <li><strong>You won't be able to launch new machines, attach volumes, or deploy changes</strong> until you add a payment method</li>
        </ul>
            <p>
              New users receive <strong>$50 in free credits</strong> upon signup, and an additional 
              <strong> $50 bonus credits</strong> when you add a credit card. These credits help you explore 
              the platform without immediate charges.
            </p>
            <p>
              Whether you're testing the waters or gearing up to launch something real, free credits give 
              you room to get started. If Aedify.AI feels like a fit, adding a payment method from the Dashboard 
              is all it takes to keep going. No surprises, no downtime, no pressure.
            </p>
      </section>

          <section id="usage-usage-dashboard" className="usage-section">
            <h2>Usage Dashboard</h2>
        <p>
              The Usage dashboard displays three key metrics at the top:
            </p>
            <ul>
              <li><strong>Balance:</strong> Amount over credits (amount due if you've exceeded your free credits)</li>
              <li><strong>This Month:</strong> Current billing cycle spending</li>
              <li><strong>Credits Left:</strong> Available credits remaining to spend</li>
        </ul>
            <p>
              These cards provide a quick overview of your current financial status and help you monitor 
              spending throughout the billing cycle.
            </p>
            {/* TODO: Replace with billing dashboard image when available */}
            <div className="usage-image">
              <img 
                src={usage1} 
                alt="Usage dashboard showing Balance, This Month, and Credits Left cards"
                className="usage-screenshot"
              />
            </div>
      </section>

          <section id="usage-cost-tracking" className="usage-section">
            <h2>Cost Tracking</h2>
        <p>
              Monitor your spending over time with interactive cost charts. View cost trends for the last 
              7 days or 30 days to understand your usage patterns.
        </p>
            <h3>Cost Over Time</h3>
            <p>
              The cost chart displays your daily spending with a smooth line graph. Hover over data points 
              to see exact costs for specific dates. This helps you identify spending trends and unusual spikes.
            </p>
            
      </section>

          <section id="usage-service-breakdown" className="usage-section">
            <h2>Service Breakdown</h2>
        <p>
              View detailed breakdowns of usage and costs by service category. Each service category shows:
            </p>
            <ul>
              <li><strong>Total Cost:</strong> Sum of all costs for that service</li>
              <li><strong>Resource Usage:</strong> Individual resource consumption (CPU-hours, GB-hours, etc.)</li>
              <li><strong>Unit Prices:</strong> Cost per unit for each resource type</li>
              <li><strong>Item Costs:</strong> Total cost for each resource item</li>
            </ul>
            <h3>App Platform</h3>
            <p>
              The App Platform category tracks compute resources:
        </p>
        <ul>
              <li><strong>vCPU Compute:</strong> CPU usage tracked per hour</li>
              <li><strong>Memory (RAM):</strong> Memory usage tracked per GB-hour</li>
        </ul>
            <p>
              Expand service categories to see detailed resource-level breakdowns. This helps you identify 
              which resources are consuming the most credits and optimize accordingly.
            </p>
          </section>

          <section id="usage-alerts" className="usage-section">
            <h2>Alerts & Notifications</h2>
            <p>
              Configure alerts to stay informed about important events related to your infrastructure, usage, 
              and billing. Alerts help you proactively manage your resources and avoid unexpected charges.
            </p>
            
            <h3>Spending Limits</h3>
            <p>
              After adding a credit card to your account, you can set a <strong>spending limit</strong> to control 
              your monthly costs. The spending limit helps you stay within budget and prevents unexpected charges.
            </p>
            <p>
              When you reach your spending limit, you have the option to <strong>pause services automatically</strong>. 
              This ensures that your spending doesn't exceed your budget. You can resume services at any time by 
              increasing your limit or manually restarting paused resources.
            </p>
            
            <h3>Managing Alerts</h3>
            <p>
              View all your active alerts, edit configurations, or disable alerts as needed. This helps you stay informed without being overwhelmed by notifications. Aedify.AI supports two types of usage alerts, depending on your payment method:
            </p>
            
            {/* Options content */}
            <div className="alert-options-container">
              <div className="alert-option-panel">
                <div className="alert-option-header">
                  <span className="alert-option-badge">OPTION A</span>
                </div>
                <h4>Alert by Amount ($)</h4>
                <div className="alert-image-item">
                  <img 
                    src={alert1} 
                    alt="Add Usage Alert modal showing $ Amount option selected"
                    className="usage-screenshot"
                  />
                </div>
                <p>
                  If you're using <strong>credits</strong> or have a <strong>credit card</strong> on file, you can set 
                  alerts based on a specific dollar amount. You'll receive an email notification when your spending 
                  reaches the specified amount.
                </p>
                {/* <p>
                  To set an amount-based alert:
                </p>
                <ol>
                  <li>Navigate to the Alerts section in your dashboard</li>
                  <li>Click "Add Usage Alert"</li>
                  <li>Select <strong>"$ Amount"</strong> as the alert type</li>
                  <li>Enter the dollar amount (e.g., $10, $50, $100)</li>
                  <li>Click "Add Alert"</li>
                </ol> */}
              </div>
              
              <div className="alert-option-panel">
                <div className="alert-option-header">
                  <span className="alert-option-badge">OPTION B</span>
                </div>
                <h4>Alert by Percentage (%)</h4>
                <div className="alert-image-item">
                  <img 
                    src={alert2} 
                    alt="Add Usage Alert modal showing % of Limit option selected"
                    className="usage-screenshot"
                  />
                </div>
                <p>
                  If you have a <strong>credit card</strong> on file and have set a spending limit, you can also set 
                  alerts based on a percentage of your limit. 
                </p>
                {/* <p>
                  To set a percentage-based alert:
                </p>
                <ol>
                  <li>Navigate to the Alerts section in your dashboard</li>
                  <li>Click "Add Usage Alert"</li>
                  <li>Select <strong>"% of Limit"</strong> as the alert type</li>
                  <li>Enter the percentage threshold (e.g., 50%, 80%, 90%)</li>
                  <li>Click "Add Alert"</li>
                </ol> */}
                <p>
                  You'll receive an email when your spending reaches the specified percentage of your spending limit. 
                  For example, if your limit is $100 and you set an alert at 80%, you'll be notified when spending 
                  reaches $80.
                </p>
              </div>
            </div>
            
            
      </section>

          <PageNavigation />
        </div>

        {/* Right Sidebar - Section Navigation */}
        <aside className="usage-sidebar">
          <div className="sidebar-section">
            <h3>Contents</h3>
            <nav className="section-nav">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#usage-${section.id}`}
                  className={`section-nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveSection(section.id)
                    document.getElementById(`usage-${section.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </div>

          <div className="sidebar-section tips-section">
            <h3>Tips</h3>
            <ul className="tips-list">
              <li>Monitor your usage dashboard regularly to track spending trends</li>
              <li>Set up billing alerts to avoid unexpected charges</li>
              <li>Review service breakdowns to identify optimization opportunities</li>
              <li>Use cost charts to understand usage patterns over time</li>
              <li>Configure alerts for critical resources and billing thresholds</li>
              <li>Stop unused resources to reduce costs</li>
              <li>Right-size instances based on actual usage data</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default Usage
